import { Fragment, useMemo, useState } from 'react';
import {
  Button,
  Text,
  Menu,
  MenuItem,
  Portal,
  Box,
  
} from '@chakra-ui/react';
import {CustomTooltip} from '../../../shared/components/CustomTooltip'
import { useCurrentRefinements, useToggleRefinement, useClearRefinements } from "react-instantsearch-hooks-web"
import { CheckIcon } from '../../../icons';

const ToggleFilters = (props) => {
  const {
    label,
    facets,
    id,
    isMulti=false,
    canNegate=false
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const { items: currentRefinements } = useCurrentRefinements();
  const attributeNames = facets.map((facet)=> {
    if(!canNegate) return facet.attribute
    else return [facet.attribute, facet.negative_attribute]
  }).flat()

  const {refine: clearAllRefinements} = useClearRefinements({includedAttributes: attributeNames})
  const active = useMemo(()=>
    currentRefinements.some(r=>attributeNames.includes(r.label)
  ), [currentRefinements])

  const facetToggles = () => 
    facets.map((facet)=> {
      const {attribute, label, negative_attribute } = facet
      // Must generate the name dynamically as we can't conditionally use the below refinement hooks.
      // So instead we ALWAYS create a negative refine hook, but conditionally use them.
      // if the "is" and "is_not" facet naming pattern isn't used on Algolia, this won't work
      const negate_attribute_name = attribute.replace("is", "is_not")
      if(canNegate && negative_attribute !== negate_attribute_name) throw new Error("If you can negate filter, negative filter must follow 'is_' and 'is_not_' pattern")
      const { value: item, refine } = useToggleRefinement({
        attribute: attribute,
      })
      const {value: negativeItem, refine: refineNegative} = useToggleRefinement({
        attribute: negate_attribute_name
      })

      const otherAttributes = useMemo(()=>attributeNames.filter((name)=>name !== attribute),[attribute])
      const {refine: clearOtherRefinements} = useClearRefinements({includedAttributes: otherAttributes})

      const handleRefine = () => {
        if(!isMulti) clearOtherRefinements()
        if(!canNegate) {
          refine({isRefined: item.isRefined})
          return
        }
        
        if(negativeItem.isRefined) refineNegative({isRefined: true})
        else if(item.isRefined){
          refine({isRefined: true})
          refineNegative({isRefined: false})
        }
        else refine({isRefined: false})
      }

      return (
        <Fragment 
          key={attribute}>
          <CustomTooltip 
            contentProps={{
              "fontFamily":"TTCOMMONSMEDIUM",
              "bgColor":'gray.900',
              "color":'gray.400',
            }}
            content={
              item.isRefined 
                ? (canNegate ? "Click again to exlude from results" : "Click again to remove filter")
                : "Click to filter"
            }>
            <MenuItem
              rounded='md'
              h='36px'
              onClick={() => handleRefine()}
              //isDisabled={!item}
              //disabled={active && !item.isRefined}
              cursor="pointer"
              color="var(--purple)"
              _hover={{
                color: "var(--orange)"
              }}
              _icon={{fill: "var(--purple)"}}
              data-cy={`${id}-option-${attribute}`}
            >
              {/* {!!icons && <Box minW="15px">
                <IconToRender width="15px" height="15px" fillColor="var(--purple)" minWidth="15px"/>
              </Box>
              } */}
              <Box 
                fontFamily="TTCOMMONSMEDIUM"
                fontSize="14px"
                textDecoration={negativeItem.isRefined && "line-through"}
                >
                {label}
              </Box>
              {item.isRefined && <Box marginLeft='auto'><CheckIcon width="15px" height="15px" fillColor="var(--purple)" minWidth="15px"/></Box>}
            </MenuItem>
          </CustomTooltip>
        </Fragment>
      )
    })
  
  return (
    <Fragment key={id}>
      <Menu.Root id={id} autoSelect={false} closeOnSelect={false} onOpenChange={(details)=>setIsOpen(details.open)} isLazy>
          <>
            <Menu.Trigger
              outline="none"
              asChild
            >
              <Button
                  display='flex'
                  alignItems='center'
                  textTransform='uppercase'
                  backgroundColor={!isOpen ? "white" : "var(--purple)"}
                  border={active || isOpen ? "solid 2px var(--purple)" : "solid 2px var(--light-purple)"}
                  height="40px"
                  borderRadius="20px"
                  padding="0px 20px"
                  variant="plain"
                  color={!isOpen ? "var(--purple)" : "white" }
                  _hover={{
                    backgroundColor: "var(--purple)",
                    color: 'white',
                    borderColor: "var(--purple)"
                  }}
                  _active={{
                    backgroundColor: "var(--purple)",
                    color: 'white',
                    borderColor: "var(--purple)"
                  }}
                  overflow='hidden'
                  data-cy={`${id}-button`}
                >
                  <Text
                    fontSize=".8rem"
                    letterSpacing="0.128em"
                    fontFamily='TTCommonsMedium'
                  > 
                  {label}
                  </Text>
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content
                  shadow='lg'
                  py='4'
                  px='3'
                  overflowY='auto'
                  minW={{ base: '13rem', smTo2xl: '15rem' }}
                  maxH='25rem'
                  borderRadius="20px"
                  background="var(--light-purple)"
                >
                  <Menu.Item
                    fontWeight='semibold'
                    rounded='md'
                    onClick={() => clearAllRefinements()}
                    position='relative'
                    cursor="pointer"
                    color='gray.400'
                    minH="13px"
                    _hover={{
                      color: "var(--purple)"
                    }}
                  >
                    <Text position='absolute' top='0px' right='0px' fontSize='0.6875rem'>
                      Reset
                    </Text>
                  </Menu.Item>
                  {!!facets && facetToggles()}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </>
      </Menu.Root>
    </Fragment>
  );
}

export default ToggleFilters